import React, { useState, useRef } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { notifyChange } from "../notifications/notify";
import { startCreativeProcessing } from "../../services/creativesService";
import { useModalMotion } from "../../components/motion/useModalMotion";
import { ModalShell } from "../../components/motion/ModalShell";
import { CreativeModalHeader, CreativePrimaryButton } from "./CreativeModalParts";
import { CreativeTargetingFields, CREATIVE_INPUT_STYLE as inputStyle, CREATIVE_LABEL_STYLE } from "./CreativeTargetingFields";
import { audioTypesLabel, dateRangeError, resolveAudioTypes } from "./creativeTargeting";
import "./NewCreativeModal.css";

const MAX_FILE_SIZE_MB = 50;

export function validateAudioFile(file) {
  if (!file.type.startsWith("audio/")) {
    return { valid: false, error: "File must be an audio format (mp3, wav, m4a, etc.)" };
  }
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return { valid: false, error: `File must be under ${MAX_FILE_SIZE_MB}MB` };
  }
  return { valid: true };
}

// Formats a freshly-saved creative as markdown for the CSM notification.
// Audio Types degrade gracefully on shape drift (objects with display_name
// → name → value); when the resolved list is empty (= "All Types") we say
// so explicitly because the UI label and the persisted [] differ. New
// creatives always start `Paused` (status_id 3) per the insert payload.
function formatNewCreativeMarkdown({ name, source, tags, audioTypes, startDate, endDate, duration }) {
  const lines = [];
  lines.push(`**Name:** ${name && name.trim() ? name.trim() : "(unnamed)"}`);
  lines.push(`**Source:** ${source === "upload" ? "Audio upload" : "Script"}`);
  if (Array.isArray(tags) && tags.length) {
    lines.push(`**Tags:** ${tags.join(", ")}`);
  }
  lines.push(`**Audio Types:** ${audioTypesLabel(audioTypes)}`);
  if (startDate) lines.push(`**Start date:** ${startDate}`);
  if (endDate) lines.push(`**End date:** ${endDate}`);
  if (duration != null) lines.push(`**Duration:** ${duration}s`);
  lines.push(`**Status:** Paused`);
  return lines.join("\n");
}

// What the busy button says while a save is in flight, step by step.
const BUSY_LABELS = { saving: "Saving…", uploading: "Uploading audio…", processing: "Starting processing…" };

export function NewCreativeModal({ advertiserId, canCreate = true, onClose, onCreated }) {
  const { isSupportUser } = useAuth();
  const [mode, setMode] = useState("script");
  const [script, setScript] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [name, setName] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [duration, setDuration] = useState(30);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [audioTypes, setAudioTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busyStep, setBusyStep] = useState("saving");
  const [saveError, setSaveError] = useState(null);
  // Escape closes, except mid-save: the creative may already be handed off.
  const motion = useModalMotion(onClose, { dismissible: !saving });
  const { requestClose } = motion;

  const switchToUpload = () => { setMode("upload"); setSaveError(null); fileInputRef.current?.click(); };
  const switchToScript = () => { setMode("script"); setSelectedFile(null); setSaveError(null); if (fileInputRef.current) fileInputRef.current.value = ""; };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setMode("script"); return; }
    const validation = validateAudioFile(file);
    if (!validation.valid) { setSaveError(validation.error); setMode("script"); e.target.value = ""; return; }
    setSaveError(null);
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    const audio = new window.Audio(url);
    audio.addEventListener("loadedmetadata", () => {
      if (audio.duration && isFinite(audio.duration)) setDuration(Math.round(audio.duration));
      URL.revokeObjectURL(url);
    });
  };

  const addTag = (value) => {
    const trimmed = value.trim();
    if (trimmed && !tags.includes(trimmed)) setTags(prev => [...prev, trimmed]);
    setTagInput("");
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); }
    else if (e.key === "Backspace" && tagInput === "" && tags.length > 0) setTags(prev => prev.slice(0, -1));
  };

  const removeTag = (index) => setTags(prev => prev.filter((_, i) => i !== index));

  const resolvedAudioTypes = resolveAudioTypes(audioTypes);
  const dateError = dateRangeError(startDate, endDate);

  const handleLaunch = async () => {
    if (!canCreate) {
      setSaveError("You have read-only access for this advertiser.");
      return;
    }
    if (!supabase || advertiserId == null) { setSaveError("Not signed in or no advertiser."); return; }
    setSaveError(null);
    setBusyStep("saving");
    setSaving(true);

    if (mode === "script") {
      const wordCount = script.trim().split(/\s+/).filter(Boolean).length;
      const estimatedDuration = Math.max(15, Math.round((wordCount / 130) * 60));
      const { error } = await supabase.from("creatives").insert({
        advertiser_id: advertiserId, script: script.trim(), name: name.trim() || null,
        duration: estimatedDuration, labels: tags, status_id: 3,
        start_date: startDate || null, end_date: endDate || null, audio_types: resolvedAudioTypes,
      });
      setSaving(false);
      if (error) { setSaveError(error.message); return; }
      // Fire-and-forget CSM notification. Skipped for admins so internal
      // QA / setup creatives don't page the CSM. Errors are swallowed inside
      // notifyChange so a Clarify/Zapier outage never blocks the modal close.
      if (!isSupportUser) {
        notifyChange({
          supabase,
          eventType: "Creative Gallery - New Creative",
          advertiserId,
          previousState: "",
          newState: formatNewCreativeMarkdown({
            name, source: "script", tags, audioTypes: resolvedAudioTypes,
            startDate, endDate, duration: estimatedDuration,
          }),
        });
      }
      onCreated?.();
      requestClose();
      return;
    }

    const { data: insertData, error: insertError } = await supabase
      .from("creatives")
      .insert({
        advertiser_id: advertiserId, script: null, name: name.trim() || null,
        duration: duration, labels: tags, status_id: 3,
        start_date: startDate || null, end_date: endDate || null, audio_types: resolvedAudioTypes,
      })
      .select("id")
      .single();

    if (insertError) { setSaving(false); setSaveError(insertError.message); return; }

    const creativeId = insertData.id;
    setBusyStep("uploading");
    const storagePath = `${advertiserId}/${creativeId}/${Date.now()}-${selectedFile.name}`;

    const { error: uploadError } = await supabase.storage
      .from("creative-uploads")
      .upload(storagePath, selectedFile, { contentType: selectedFile.type });

    if (uploadError) {
      await supabase.from("creatives").delete().eq("id", creativeId);
      setSaving(false);
      setSaveError("Audio upload failed. Please try again.");
      return;
    }

    const rollBack = async (message) => {
      await Promise.all([
        supabase.storage.from("creative-uploads").remove([storagePath]),
        supabase.from("creatives").delete().eq("id", creativeId),
      ]);
      setSaving(false);
      setSaveError(message);
    };

    setBusyStep("processing");
    const { error: processError, handedOff } = await startCreativeProcessing({ advertiserId, creativeId, storagePath });
    // Once handed off the creative keeps its variation (failed or processing) and shows in
    // the list; deleting it could orphan a Portal asset that is already transcoding.
    if (processError && !handedOff) {
      await rollBack("We couldn't start processing your audio. Please try again.");
      return;
    }

    setSaving(false);
    // Fire-and-forget CSM notification. See script branch for rationale.
    // Fired AFTER the creative is handed to a pipeline so the row is fully persisted; if
    // anything earlier rolled the insert back, we never reach this point.
    if (!isSupportUser) {
      notifyChange({
        supabase,
        eventType: "Creative Gallery - New Creative",
        advertiserId,
        previousState: "",
        newState: formatNewCreativeMarkdown({
          name, source: "upload", tags, audioTypes: resolvedAudioTypes,
          startDate, endDate, duration,
        }),
      });
    }
    onCreated?.();
    requestClose();
  };

  const isSubmitDisabled = !canCreate || saving || !!dateError || (mode === "script" ? script.trim().length < 10 : !selectedFile);

  return (
    <ModalShell motion={motion} labelledBy="new-creative-title" maxWidth={560}>

        <CreativeModalHeader
          eyebrow="New Creative"
          title={mode === "upload" ? "Upload your audio" : "Add a script"}
          titleId="new-creative-title"
          onClose={requestClose}
          closeDisabled={saving}
        />

        <div style={{ padding:"24px 28px" }}>

          <div key={mode} className={`ncm-swap--${mode}`}>
          {mode === "script" ? (
            <>
              <div style={{ fontSize:13, color:"#626262", marginBottom:10 }}>
                Paste your script below, or{" "}
                <button onClick={switchToUpload} style={{ background:"none", border:"none", color:"#F90091", fontSize:13, cursor:"pointer", padding:0, textDecoration:"underline", fontFamily:"inherit" }}>
                  upload your audio file
                </button>.
              </div>
              <textarea
                value={script} onChange={e => setScript(e.target.value)}
                placeholder="Paste your ad script here..."
                style={{ width:"100%", minHeight:160, background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:12, padding:16, color:"#151515", fontSize:13, lineHeight:1.6, resize:"vertical", outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}
              />
              <div style={{ marginTop:8, marginBottom:16 }}>
                <span style={{ fontSize:11, color: script.split(/\s+/).filter(Boolean).length > 80 ? "#e53935" : "#7b7b7b" }}>
                  {script.split(/\s+/).filter(Boolean).length} words
                </span>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize:13, color:"#626262", marginBottom:10 }}>
                Upload your audio file, or{" "}
                <button onClick={switchToScript} style={{ background:"none", border:"none", color:"#F90091", fontSize:13, cursor:"pointer", padding:0, textDecoration:"underline", fontFamily:"inherit" }}>
                  use a script instead
                </button>.
              </div>
              <div
                className="ncm-dropzone"
                onClick={() => fileInputRef.current?.click()}
                style={{ border:"2px dashed #E2E2E2", borderRadius:12, padding:"28px 16px", textAlign:"center", cursor:"pointer", marginBottom:16, background: selectedFile ? "#FFF5FB" : "#FAFAFA" }}
              >
                {selectedFile ? (
                  <div key={selectedFile.name} className="ncm-land">
                    <div style={{ fontSize:13, color:"#F90091", fontWeight:600, marginBottom:4 }}>{selectedFile.name}</div>
                    <div style={{ fontSize:11, color:"#7b7b7b" }}>{(selectedFile.size / (1024*1024)).toFixed(2)} MB · {duration}s detected</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize:13, color:"#7b7b7b", marginBottom:4 }}>Click to select audio file</div>
                    <div style={{ fontSize:11, color:"#B8B8B8" }}>MP3, WAV, M4A — max 50MB</div>
                  </div>
                )}
              </div>
            </>
          )}
          </div>

          <input ref={fileInputRef} type="file" accept="audio/*" aria-label="Audio file" onChange={handleFileChange} style={{ display:"none" }} />

          <div style={{ marginBottom:16 }}>
            <label style={CREATIVE_LABEL_STYLE}>Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Variant A — Emotional" style={inputStyle} />
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={CREATIVE_LABEL_STYLE}>Tags (optional)</label>
            <div style={{ ...inputStyle, display:"flex", flexWrap:"wrap", gap:6, alignItems:"center", cursor:"text", minHeight:40, padding:"6px 10px" }} onClick={() => document.getElementById("tag-input")?.focus()}>
              {tags.map((t, i) => (
                <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, padding:"2px 8px", background:"#EEF2FF", color:"#323084", borderRadius:4 }}>
                  {t}
                  <button onClick={() => removeTag(i)} style={{ background:"none", border:"none", color:"#323084", cursor:"pointer", padding:0, fontSize:12, lineHeight:1 }}>×</button>
                </span>
              ))}
              <input
                id="tag-input" type="text" value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                onBlur={() => tagInput.trim() && addTag(tagInput)}
                placeholder={tags.length === 0 ? "e.g. Variant A" : ""}
                style={{ flex:1, minWidth:80, background:"transparent", border:"none", outline:"none", color:"#151515", fontSize:13, fontFamily:"inherit", padding:0 }}
              />
            </div>
          </div>

          <CreativeTargetingFields
            startDate={startDate} endDate={endDate} audioTypes={audioTypes}
            onStartDateChange={setStartDate} onEndDateChange={setEndDate} onAudioTypesChange={setAudioTypes}
          />

          {saveError && <div key={saveError} role="alert" className="ncm-reveal" style={{ marginBottom:12, fontSize:12, color:"#e53935" }}>{saveError}</div>}
          <CreativePrimaryButton onClick={handleLaunch} disabled={isSubmitDisabled} busy={saving}>
            {saving ? BUSY_LABELS[busyStep] : mode === "upload" ? "Upload & Activate" : "Launch Creative"}
          </CreativePrimaryButton>
        </div>
    </ModalShell>
  );
}
